import { useDeferredValue, useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { getAllGammaLines, getAllLineFamilies } from "../services/categoriesService";
import { getAllDownloadDocuments } from "../services/downloadService";
import { getAllGammas } from "../services/gammasService";
import { getAllJobOffers } from "../services/recruitmentService";
import { withLocalePath } from "../utils/localeRouting";

const normalizeText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s/-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const createResult = ({ id, label, path, section, keywords = [] }) => ({
  id,
  label,
  path,
  section,
  searchableText: normalizeText([label, section, ...keywords].join(" ")),
});

const getScore = (result, normalizedQuery) => {
  if (!normalizedQuery) return 0;

  const label = normalizeText(result.label);

  if (label === normalizedQuery) return 400;
  if (label.startsWith(normalizedQuery)) return 300;
  if (label.includes(normalizedQuery)) return 200;
  if (result.searchableText.includes(normalizedQuery)) return 100;

  return -1;
};

const languageLabels = {
  pt: {
    products: "Produtos",
    aboutUs: "Quem Somos",
    downloadArea: "Area de Download",
    recruitment: "Recrutamento",
    contacts: "Contactos",
    page: "Pagina",
    productsSection: "Produtos",
    recruitmentSection: "Recrutamento",
    downloadSection: "Download",
  },
  en: {
    products: "Products",
    aboutUs: "About Us",
    downloadArea: "Downloads",
    recruitment: "Recruitment",
    contacts: "Contacts",
    page: "Page",
    productsSection: "Products",
    recruitmentSection: "Recruitment",
    downloadSection: "Download",
  },
  es: {
    products: "Productos",
    aboutUs: "Quienes somos",
    downloadArea: "Area de Descarga",
    recruitment: "Contratacion",
    contacts: "Contactos",
    page: "Pagina",
    productsSection: "Productos",
    recruitmentSection: "Contratacion",
    downloadSection: "Descarga",
  },
  fr: {
    products: "Produits",
    aboutUs: "Qui nous sommes",
    downloadArea: "Zone de telechargement",
    recruitment: "Recrutement",
    contacts: "Contacts",
    page: "Page",
    productsSection: "Produits",
    recruitmentSection: "Recrutement",
    downloadSection: "Telechargement",
  },
};

export const useHeaderSearch = (query, enabled) => {
  const { language } = useAuth();
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = normalizeText(deferredQuery);
  const labels = languageLabels[language] || languageLabels.pt;

  const { data: gammas = [], isLoading: loadingGammas } = useQuery({
    queryKey: ["header-search-gammas", language],
    queryFn: () => getAllGammas(language),
    enabled: enabled && !!language,
    staleTime: 5 * 60 * 1000,
  });

  const gammaLinesQueries = useQueries({
    queries: (gammas || []).map((gamma) => ({
      queryKey: ["header-search-gamma-lines", gamma.path, language],
      queryFn: getAllGammaLines(gamma.path, language),
      enabled: enabled && !!language && !!gamma.path,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const flattenedLines = useMemo(
    () =>
      (gammas || []).flatMap((gamma, index) =>
        (gammaLinesQueries[index]?.data?.successors || []).map((line) => ({
          gamma,
          line,
        }))
      ),
    [gammaLinesQueries, gammas]
  );

  const lineFamiliesQueries = useQueries({
    queries: flattenedLines.map(({ gamma, line }) => ({
      queryKey: ["header-search-line-families", gamma.path, line.path, language],
      queryFn: getAllLineFamilies(gamma.path, line.path, language),
      enabled: enabled && !!language && !!gamma.path && !!line.path,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const { data: documents = [], isLoading: loadingDocuments } = useQuery({
    queryKey: ["header-search-downloads", language],
    queryFn: () => getAllDownloadDocuments(language),
    enabled: enabled && !!language,
    staleTime: 5 * 60 * 1000,
  });

  const { data: jobs = [], isLoading: loadingJobs } = useQuery({
    queryKey: ["header-search-jobs", language],
    queryFn: () => getAllJobOffers(language),
    enabled: enabled && !!language,
    staleTime: 5 * 60 * 1000,
  });

  const searchIndex = useMemo(() => {
    const staticPages = [
      createResult({
        id: "page-products",
        label: labels.products,
        path: withLocalePath("/products", language),
        section: labels.page,
      }),
      createResult({
        id: "page-aboutus",
        label: labels.aboutUs,
        path: withLocalePath("/aboutus", language),
        section: labels.page,
      }),
      createResult({
        id: "page-downloadarea",
        label: labels.downloadArea,
        path: withLocalePath("/downloadarea", language),
        section: labels.page,
      }),
      createResult({
        id: "page-recruitment",
        label: labels.recruitment,
        path: withLocalePath("/recruitment", language),
        section: labels.page,
      }),
      createResult({
        id: "page-contacts",
        label: labels.contacts,
        path: withLocalePath("/contacts", language),
        section: labels.page,
      }),
    ];

    const gammaResults = (gammas || []).map((gamma) =>
      createResult({
        id: `gamma-${gamma.id ?? gamma.path}`,
        label: gamma.name,
        path: gamma.lines?.length > 1
          ? withLocalePath(`/products/${gamma.path}`, language)
          : withLocalePath(`/products/${gamma.path}/${gamma.lines?.[0]?.path ?? ""}`, language),
        section: labels.productsSection,
      })
    );

    const lineResults = flattenedLines.map(({ gamma, line }) =>
      createResult({
        id: `line-${line.id ?? `${gamma.path}-${line.path}`}`,
        label: line.name,
        path: withLocalePath(`/products/${gamma.path}/${line.path}`, language),
        section: gamma.name,
        keywords: [gamma.name],
      })
    );

    const familyResults = flattenedLines.flatMap(({ gamma, line }, index) =>
      (lineFamiliesQueries[index]?.data?.successors || []).map((family) =>
        createResult({
          id: `family-${family.id ?? `${gamma.path}-${line.path}-${family.path}`}`,
          label: family.name,
          path: withLocalePath(
            `/products/${gamma.path}/${line.path}/${family.path || family.id}`,
            language
          ),
          section: line.name,
          keywords: [gamma.name, line.name],
        })
      )
    );

    const downloadResults = (documents || []).flatMap((documentGroup) =>
      (documentGroup.lines || []).flatMap((line) =>
        (line.families || []).map((family) => {
          const familyPath = family.path;
          const hasProductPath =
            familyPath?.gammaPath && familyPath?.linePath && familyPath?.familyPath;

          return createResult({
            id: `download-${family.id}-${family.documentId}`,
            label: family.name,
            path: hasProductPath
              ? withLocalePath(
                  `/products/${familyPath.gammaPath}/${familyPath.linePath}/${familyPath.familyPath}`,
                  language
                )
              : withLocalePath("/downloadarea", language),
            section: documentGroup.type?.name || labels.downloadSection,
            keywords: [line.name],
          });
        })
      )
    );

    const jobResults = (jobs || []).map((job) =>
      createResult({
        id: `job-${job.id ?? job.path}`,
        label: job.title,
        path: withLocalePath(`/recruitment/${job.path}`, language),
        section: job.location?.name || labels.recruitmentSection,
        keywords: [job.jobType?.name],
      })
    );

    const deduped = new Map();

    [...staticPages, ...gammaResults, ...lineResults, ...familyResults, ...downloadResults, ...jobResults].forEach(
      (result) => {
        const key = `${result.path}-${normalizeText(result.label)}`;

        if (!deduped.has(key)) {
          deduped.set(key, result);
        }
      }
    );

    return Array.from(deduped.values());
  }, [documents, flattenedLines, gammas, jobs, labels, language, lineFamiliesQueries]);

  const results = useMemo(() => {
    if (!normalizedQuery) return [];

    return searchIndex
      .map((result) => ({
        ...result,
        score: getScore(result, normalizedQuery),
      }))
      .filter((result) => result.score >= 0)
      .sort((left, right) => right.score - left.score || left.label.localeCompare(right.label, language))
      .slice(0, 8);
  }, [language, normalizedQuery, searchIndex]);

  return {
    results,
    isLoading:
      loadingGammas ||
      loadingDocuments ||
      loadingJobs ||
      gammaLinesQueries.some((queryItem) => queryItem.isLoading) ||
      lineFamiliesQueries.some((queryItem) => queryItem.isLoading),
  };
};
